// ── Web Push helpers ─────────────────────────────────────────────
// Prompt once → subscribe → save the device. Sends go through the
// `send-push` Supabase Edge Function (which holds the VAPID private key).
import { supabase, ORG_ID } from './supabaseClient';

// Public VAPID key (safe to embed in the client).
const VAPID_PUBLIC = 'BIEMhyy8b3p12VMJTn2OxzmhYSL1f0bSTQt7oolqmb5RVT4Ow1uVgMXFmJWNAzQg6wO8c8zW2oDuUeoS43GcKkU';

export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function pushPermission() {
  return pushSupported() ? Notification.permission : 'unsupported';
}

function urlB64ToUint8(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// Ask for permission (if needed), subscribe this device, and save it.
// silent=true skips the prompt and only refreshes an existing grant.
export async function enablePush(empId, silent) {
  if (!pushSupported() || !empId) return { ok: false, reason: 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') {
    if (silent) return { ok: false, reason: 'default' };
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') return { ok: false, reason: perm };
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(VAPID_PUBLIC),
      });
    }
    const j = sub.toJSON();
    await supabase.from('push_subscriptions').upsert({
      org_id: ORG_ID,
      endpoint: sub.endpoint,
      emp_id: String(empId),
      p256dh: j.keys.p256dh,
      auth: j.keys.auth,
      ua: navigator.userAgent,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'org_id,endpoint' });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: (e && e.message) || 'subscribe-failed' };
  }
}

// Fire a push to a recipient (empId or 'admin'). Fire-and-forget. Staff calls are
// allowed by their sign-in; the client review page has no sign-in, so it passes its
// share link, and send-push only lets that notify the mockup's own creator.
export function sendPush(empId, title, body, url, shareId) {
  if (!empId) return;
  try {
    supabase.functions.invoke('send-push', {
      body: { empId: String(empId), title: title || 'Strategists', body: body || '', url: url || '/', ...(shareId ? { shareId } : {}) },
    }).catch(() => {});
  } catch (e) { /* non-fatal */ }
}
